import { omit } from 'lodash';

import { LettingTransactionsCreateDTO } from '@apis/transactionsLettingsApi.types';
import { SaleTransactionsCreateDTO } from '@apis/transactionsSalesApi.types';
import { FieldToggleItem } from '@shared/components/atlas-transaction-form/components/FieldToggleList/FieldToggleList.types';
import { MixedUser } from '@shared/components/lookups/user-lookup';
import { AddressPayload } from '@shared/components/update-address-drawer';
import { LettingTransactionTypes } from '@shared/models/transactions/lettings/lettings';
import { formatDate } from '@shared/utils/date';

import { CreateTransactionFormData } from './CreateTransactionDrawer';
import { CreateTransactionTypes } from './types';

type ExistingUserLookupItem = Extract<MixedUser, { id: number }>;

const isExistingUser = (user: MixedUser): user is ExistingUserLookupItem => {
  return 'id' in user;
};

const deriveAgencyRole = (ourDisposal: boolean, ourAcquisition: boolean): number | null => {
  if (ourDisposal && !ourAcquisition) return 0;
  if (!ourDisposal && ourAcquisition) return 1;
  if (ourDisposal && ourAcquisition) return 2;
  return null;
};

const buildAtlasPublishDetails = (
  shouldPublish: boolean | undefined,
  fieldList: FieldToggleItem[] | undefined,
  fieldVisibility: Record<string, boolean> | undefined
): Record<string, { enabled: boolean; title: string }> | null => {
  if (!shouldPublish || !fieldList) {
    return null;
  }

  const initialAcc: Record<string, { enabled: boolean; title: string }> = {};

  const result = fieldList.reduce((acc, field) => {
    const isVisible = fieldVisibility?.[field.key] !== undefined ? fieldVisibility[field.key] : field.defaultVisible;

    acc[field.key] = {
      enabled: !!isVisible,
      title: field.displayName,
    };

    return acc;
  }, initialAcc);

  return result;
};

const buildFloorUnitsPayload = (floorUnits: CreateTransactionFormData['floorUnits'], sizeMetric: Metric) => {
  const result = floorUnits
    .filter((floor) => floor.include === 1)
    .map((floor) => ({
      ...omit(floor, ['id', 'unit_string']),
      unit: floor.unit || floor.unit_string || null,
      size_metric: sizeMetric,
      fitout_condition: floor.fitout_condition || '',
      building_types: floor.property_types?.[0] || '',
      part_floor: floor.floor === 'unit' ? false : floor.part_floor,
    }));

  return result;
};

const buildAgentUsersString = (agents: MixedUser[]): string | null => {
  if (!agents.length) {
    return null;
  }

  const result = agents
    .filter(isExistingUser)
    .map((agent) => agent.id)
    .join(', ');

  return result || null;
};

export const serialiseLettingTransaction = (
  formData: CreateTransactionFormData,
  createType: CreateTransactionTypes,
  addressData: AddressPayload,
  ourAcquisition: NumberBoolean,
  ourDisposal: NumberBoolean,
  shouldPublish?: boolean,
  fieldVisibility?: Record<string, boolean>,
  lettingsFieldList?: FieldToggleItem[]
): LettingTransactionsCreateDTO => {
  let lettingType: LettingTransactionTypes | null = null;
  if (createType === CreateTransactionTypes.LEASE) {
    lettingType = LettingTransactionTypes.NEW_LEASE;
  } else if (createType === CreateTransactionTypes.LEASE_RENEWAL) {
    lettingType = LettingTransactionTypes.RENEWAL;
  } else if (createType === CreateTransactionTypes.RENT_REVIEW) {
    lettingType = LettingTransactionTypes.RENT_REVIEW;
  }

  const atlasPublishDetails = buildAtlasPublishDetails(shouldPublish, lettingsFieldList, fieldVisibility);

  const assignedUserId = formData.assignee?.id;

  // formData.transactionType is number (user selection) or lettingType derived from createType
  // Both represent LettingTransactionTypes enum values
  const resolvedType = formData.transactionType ?? lettingType;
  const transactionType: LettingTransactionTypes = resolvedType as LettingTransactionTypes;

  const result = {
    address: addressData,
    details: {
      assigned_users: assignedUserId ? [assignedUserId] : [],
      building_types: formData.buildingTypes,
      close_disposal: 0,
      comments: null,
      tenant_companies: formData.company && 'id' in formData.company ? [formData.company.id] : null,
      entire_property: formData.entireProperty ? 1 : 0,
      floor_units: buildFloorUnitsPayload(formData.floorUnits, formData.sizeMetric),
      landlord_companies: formData.landlord
        ? 'id' in formData.landlord
          ? [formData.landlord.id]
          : [{ name: formData.landlord.name }]
        : null,
      lease_end_readable: formData.leaseEndReadable || null,
      lease_end: formatDate(formData.leaseEnd, 'YYYY-MM-DD') || null,
      rent_free_events: formData.rentFreeEvents.map((event) => ({
        comments: event.comments,
        date_days: event.date_days,
        date_months: event.date_months,
        date_priority: event.date_priority,
        date_years: event.date_years,
        from: event.from,
        reminder_days: event.reminder_days,
        to: event.to,
        type: event.type,
      })),
      lease_break_events: formData.leaseBreaks.map((leaseBreak) => ({
        break_option: leaseBreak.break_option,
        break_penalty: leaseBreak.break_penalty ? leaseBreak.break_penalty : '',
        break_penalty_comments: leaseBreak.break_penalty_comments,
        comments: leaseBreak.comments,
        date_days: leaseBreak.date_days,
        date_months: leaseBreak.date_months,
        date_priority: leaseBreak.date_priority ?? false,
        date_years: leaseBreak.date_years,
        from: leaseBreak.from,
        to: leaseBreak.from,
        id: null,
        notice_months: leaseBreak.notice_months ? leaseBreak.notice_months : '',
        reminder_days: leaseBreak.reminder_days,
      })),
      rent_review_events: formData.rentReviews.map((rentReview) => ({
        comments: rentReview.comments,
        date_days: rentReview.date_days,
        date_months: rentReview.date_months,
        date_priority: rentReview.date_priority,
        date_years: rentReview.date_years,
        from: rentReview.from || formData.leaseStart,
        to: rentReview.from,
        id: null,
        reminder_days: rentReview.reminder_days,
        upward_only: rentReview.upward_only,
      })),
      capital_incentives_list: formData.capitalIncentives.map((incentive) => ({
        amount: incentive.amount,
        comments: incentive.comments,
      })),
      heads_of_terms: formData.headsOfTerms || null,
      transaction_notes: formData.transactionNotes || null,
      lease_expired: null,
      lease_length_days: formData.leaseLengthDays ? Number(formData.leaseLengthDays) : null,
      lease_length_months: formData.leaseLengthMonths ? Number(formData.leaseLengthMonths) : null,
      lease_length_years: formData.leaseLengthYears ? Number(formData.leaseLengthYears) : null,
      lease_start: formatDate(formData.leaseStart, 'YYYY-MM-DD') || null,
      type: transactionType,
      match_id: formData.tenantRadio === '0' ? null : null,
      rent_currency: formData.rentCurrency,
      rent_metric: formData.rentMetric || null,
      rent_rates: formData.rentRates,
      rent: formData.rent ? Number(formData.rent) : null,
      sectors: formData.sectors.length ? formData.sectors : [],
      signature_date: formatDate(formData.transactionDate, 'YYYY-MM-DD') || null,
      size_metric: formData.sizeMetric,
      size_measure: formData.sizeMeasure || null,
      size: formData.sizeValue ? Number(formData.sizeValue) : null,
      source: formData.source ? Number(formData.source) : null,
      our_acquisition: ourAcquisition,
      our_disposal: ourDisposal,
      tags: formData.selectedTagIds.join(','),
      agency_role: deriveAgencyRole(formData.ourDisposal, formData.ourAcquisition),
      landlord_agent_users: buildAgentUsersString(formData.landlordAgents),
      tenant_agent_users: buildAgentUsersString(formData.tenantAgents),
      publish_to_atlas: shouldPublish || false,
      atlas_publish_details: atlasPublishDetails,
    },
    property_id: formData.existingPropertyId || null,
  };

  return result;
};

export const serialiseSaleTransaction = (
  formData: CreateTransactionFormData,
  createType: CreateTransactionTypes,
  addressData: AddressPayload,
  ourAcquisition: NumberBoolean,
  ourDisposal: NumberBoolean,
  shouldPublish?: boolean,
  fieldVisibility?: Record<string, boolean>,
  salesFieldList?: FieldToggleItem[]
): SaleTransactionsCreateDTO => {
  const atlasPublishDetails = buildAtlasPublishDetails(shouldPublish, salesFieldList, fieldVisibility);

  const assignedUserId = formData.assignee?.id;

  const result = {
    address: addressData,
    details: {
      assigned_users: assignedUserId ? [assignedUserId] : [],
      building_types: formData.buildingTypes.length ? formData.buildingTypes : null,
      close_disposal: 0,
      comment: null,
      cap_val: formData.capital ? Number(formData.capital) : null,
      cap_val_currency: formData.capitalCurrency,
      purchaser_companies: formData.company && 'id' in formData.company ? [formData.company.id] : null,
      completion_date: formatDate(formData.transactionDate, 'YYYY-MM-DD') || null,
      floor_units: buildFloorUnitsPayload(formData.floorUnits, formData.sizeMetric),
      heads_of_terms: formData.headsOfTerms || null,
      investment: (createType === CreateTransactionTypes.INVESTMENT_SALE ? 1 : 0) as NumberBoolean,
      lease_comment: formData.leaseComment ?? null,
      lease_expiry_month: formData.leaseExpiryMonth ? Number(formData.leaseExpiryMonth) : null,
      lease_expiry_year: formData.leaseExpiryYear || null,
      match_id: null,
      passing_niy: formData.netInitialYield ? Number(formData.netInitialYield) : undefined,
      passing_rent: formData.passingRent ? Number(formData.passingRent) : null,
      passing_rent_currency: formData.passingRentCurrency || formData.purchasePriceCurrency,
      passing_rent_metric: (formData.passingRentMetric || null) as Metric | null,
      purchase_price: formData.purchasePrice ? Number(formData.purchasePrice) : null,
      purchase_price_currency: formData.purchasePriceCurrency,
      purchaser_agent_basic: null,
      purchaser_basic: null,
      sale_type: formData.saleType ? Number(formData.saleType) : null,
      sectors: formData.sectors.length ? formData.sectors : [],
      size_metric: formData.sizeMetric || null,
      size_measure: formData.sizeMeasure || null,
      size: formData.sizeValue ? Number(formData.sizeValue) : null,
      source: formData.source ? Number(formData.source) : null,
      transaction_notes: formData.transactionNotes || null,
      vendor_agent_basic: null,
      vendor_basic: null,
      vendor_companies: formData.vendor
        ? 'id' in formData.vendor
          ? [formData.vendor.id]
          : [{ name: formData.vendor.name }]
        : null,
      wault_to_break: formData.waultToBreak ? Number(formData.waultToBreak) : undefined,
      wault_to_expiry: formData.waultToExpiry ? Number(formData.waultToExpiry) : undefined,
      entire_property: formData.entireProperty ? 1 : 0,
      our_acquisition: ourAcquisition,
      our_disposal: ourDisposal,
      tags: formData.selectedTagIds.join(','),
      agency_role: deriveAgencyRole(formData.ourDisposal, formData.ourAcquisition),
      vendor_agent_users: buildAgentUsersString(formData.landlordAgents),
      purchaser_agent_users: buildAgentUsersString(formData.tenantAgents),
      publish_to_atlas: shouldPublish || false,
      atlas_publish_details: atlasPublishDetails,
    },
    property_id: formData.existingPropertyId || null,
  };

  return result;
};
